import {
  Auth0Session,
  DevelopmentSession,
  type DestinationClient,
  type DestinationSession,
} from '@traceio-org/auth-ui';
import {
  createPlatformClient,
  type PlatformAdminMethods,
  type PlatformBillingMethods,
  type PlatformClient,
  type PlatformTicketMethods,
} from '@traceio-org/platform-client';
import type { AdminConfig } from './config';

/** The staff ticket methods of the client (ADMIN_TICKETS_API): every ticket method whose name starts with admin. */
export type AdminTicketMethods = Pick<
  PlatformTicketMethods,
  Extract<keyof PlatformTicketMethods, `admin${string}`>
>;

/**
 * The platform client calls Trace Admin makes: the gate's, the registry, and the staff reads and writes
 * (every staff method of the client except the internal side-effect authorization, which is for services),
 * the staff billing methods included (milestone 3b), and the self-serve staff methods (spec 2026-10-18 piece 8:
 * the switch and the sign-ups needing attention).
 */
export type AdminClient = DestinationClient &
  Pick<
    PlatformClient,
    | 'applications'
    | 'adminOrganizations'
    | 'adminOrganization'
    | 'adminSelfServeSettings'
    | 'adminUpdateSelfServeSettings'
    | 'adminSelfServeSignups'
    | 'adminRetrySelfServeSignup'
  > &
  Omit<PlatformAdminMethods, 'authorizeAction'> &
  PlatformBillingMethods &
  AdminTicketMethods;

/** Everything Trace Admin talks to; tests replace any part. */
export interface AdminDependencies {
  client: AdminClient;
  /** The session for the registry's admin audience: Auth0 when configured, else development identity. */
  createSession: (audience: string) => DestinationSession;
  /** This page's origin, checked against the registry. */
  origin: string;
  /** Full-page navigation used by the gate (the browser's by default). */
  assign?: (url: string) => void;
}

export function createAdminDependencies(config: AdminConfig): AdminDependencies {
  // Tokens are passed per call (by the gate and by the staff reads), so the client needs no token source.
  const client = createPlatformClient({ baseUrl: config.platformApiUrl });
  return {
    client,
    origin: window.location.origin,
    createSession: (audience) =>
      config.auth0
        ? new Auth0Session({ domain: config.auth0.domain, clientId: config.auth0.clientId, audience })
        : // Development identity: the session the hub started, shared through platform-api's cookie.
          new DevelopmentSession({ client, defaultAudience: audience }),
  };
}
